<template>
    <div>
        <!-- Loading skeleton -->
        <div v-if="loading" data-testid="cesion-detail-loading" class="tw:space-y-5">
            <!-- Event card skeleton -->
        </div>

        <div v-else-if="error" data-testid="cesion-detail-error" class="tw:p-4 tw:rounded-lg tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-700">
            <div class="tw:flex tw:items-start tw:gap-3">
                <i class="material-icons tw:text-red-500">error_outline</i>
                <div>
                    <p class="tw:font-semibold">{{ t('perfil.cesion_error_cargar_estado') }}</p>
                    <p class="tw:text-sm tw:mt-1">{{ error }}</p>
                </div>
            </div>
        </div>

        <div v-else-if="estado" data-testid="cesion-estado" class="tw:space-y-5">
            <div v-if="evento" class="tw:overflow-hidden tw:rounded-[32px] tw:border tw:border-slate-200 tw:bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.03),_rgba(255,255,255,0.98)_50%)] tw:p-4 tw:shadow-[0_24px_60px_-38px_rgba(15,23,42,0.45)] sm:tw:p-5">
                <div class="tw:mb-4 tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-[0.24em] tw:text-slate-400">
                    <span>{{ evento.fecha }}</span>
                    <span v-if="evento.hora">• {{ evento.hora }}</span>
                    <span v-if="evento.fecha_provisional" class="tw:max-w-full tw:rounded-full tw:bg-amber-100 tw:px-2.5 tw:py-1 tw:text-[11px] tw:text-amber-800 tw:break-words">
                        {{ t('perfil.cesion_fecha_provisional') }}
                    </span>
                </div>

                <Matchup
                    :local-team="evento.equipo_local"
                    :away-team="evento.equipo_visitante"
                    :division="evento.division"
                    :show-status="true"
                    :status="evento.cesion_temporal_activa ? t('perfil.cesion_estado_cedido') : t('perfil.cesion_estado_disponible')"
                />
            </div>

            <div v-if="estado.cedido" class="tw:rounded-[28px] tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-5">
                <h3 class="tw:font-semibold tw:text-blue-900 tw:mb-2">{{ t('perfil.cesion_asiento_cedido_titulo') }}</h3>
                <p class="tw:text-sm tw:text-blue-900 tw:mb-1">
                    <strong>{{ t('perfil.cesion_cedido_a') }}</strong> {{ estado.acceso_cedido_user_nombre }} {{ estado.acceso_cedido_user_apellidos }}
                </p>
                <p class="tw:text-sm tw:text-blue-900 tw:mb-3">
                    <strong>{{ t('perfil.cesion_fecha') }}</strong> {{ formatFecha(estado.cedido_at) }}
                </p>
                <Button
                    v-if="estado.acceso_cedido_pdf_url"
                    as="a"
                    :href="estado.acceso_cedido_pdf_url"
                    target="_blank"
                    icon="fas fa-download"
                    variant="alt"
                    :custom-class="'tw:inline-flex tw:items-center tw:gap-2'"
                >
                    {{ t('perfil.cesion_descargar_acceso') }}
                </Button>
            </div>

            <div
                v-if="!estado.puede_ceder && !estado.puede_recuperar && estado.motivo_no_disponible"
                class="tw:rounded-[28px] tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-5 tw:text-amber-900"
            >
                <p class="tw:text-sm">
                    <strong>{{ t('perfil.cesion_razon') }}</strong> {{ estado.motivo_no_disponible }}
                </p>
            </div>

            <div v-if="estado.ultimo_movimiento" class="tw:rounded-[28px] tw:border tw:border-slate-200 tw:bg-white tw:p-5">
                <h3 class="tw:font-semibold tw:text-slate-900 tw:mb-2">{{ t('perfil.cesion_ultimo_movimiento') }}</h3>
                <p class="tw:text-sm tw:text-slate-900 tw:mb-1">
                    <strong>{{ t('perfil.cesion_accion') }}</strong> {{ formatUltimoMovimientoAccion(estado.ultimo_movimiento.accion) }}
                </p>
                <p class="tw:text-sm tw:text-slate-600">
                    <strong>{{ t('perfil.cesion_fecha') }}</strong> {{ formatFecha(estado.ultimo_movimiento.fecha) }}
                </p>
            </div>

            <div
                v-if="errorOperacion"
                class="tw:flex tw:items-start tw:gap-3 tw:rounded-[28px] tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:text-red-900"
            >
                <i class="material-icons tw:text-red-500">warning</i>
                <p class="tw:text-sm">{{ errorOperacion }}</p>
            </div>

            <div class="tw:flex tw:flex-col tw:gap-3 tw:pt-4 sm:tw:flex-row">
                <Button
                    v-if="estado.puede_ceder"
                    type="button"
                    variant="alt"
                    icon="fas fa-check"
                    :loading="loadingOperacion"
                    :custom-class="'tw:w-full tw:flex-1 tw:font-semibold'"
                    data-testid="cesion-btn-ceder"
                    @click="showCederForm = true"
                >
                    {{ t('perfil.cesion_ceder_asiento') }}
                </Button>

                <Button
                    v-if="estado.puede_recuperar"
                    type="button"
                    variant="alt"
                    icon="fas fa-undo"
                    :loading="loadingOperacion"
                    :custom-class="'tw:w-full tw:flex-1 tw:font-semibold'"
                    data-testid="cesion-btn-recuperar"
                    @click="onRecuperarClick"
                >
                    {{ t('perfil.cesion_recuperar_asiento') }}
                </Button>
            </div>

            <div v-if="showCederForm && estado.puede_ceder" data-testid="cesion-form-container">
                <Form
                    :loading="loadingOperacion"
                    :error="errorOperacion"
                    @submit="onCederSubmit"
                    @cancel="showCederForm = false"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import { useFilters } from '@/composables/front/formatters/useFilters';
    import Button from '@/modules/front/ui/Button.vue';
    import Matchup from '@/modules/front/perfil/components/cesion/Matchup.vue';
    import Form from './Form.vue';
    import { SkeletonItem } from '@/modules/front/ui/skeleton';

    const props = defineProps({
        evento: {
            type: Object,
            default: null
        },
        estado: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        loadingOperacion: {
            type: Boolean,
            default: false
        },
        errorOperacion: {
            type: String,
            default: null
        }
    });

    // Close form when operation completes successfully (loadingOperacion goes false with no error)
    watch(() => props.loadingOperacion, (newVal, oldVal) => {
        if (oldVal === true && newVal === false && !props.errorOperacion) {
            showCederForm.value = false;
        }
    });

    const emit = defineEmits(['ceder', 'recuperar']);

    const { formatearFecha } = useFilters();
    const showCederForm = ref(false);

    const formatFecha = fecha => formatearFecha(fecha) ?? '—';

    const ultimoMovimientoLabels = {
        cedida: 'perfil.cesion_accion_cedida',
        recuperada: 'perfil.cesion_accion_recuperada',
        acceso_original_invalidado: 'perfil.cesion_accion_acceso_original_invalidado',
        acceso_cedido_generado: 'perfil.cesion_accion_acceso_cedido_generado',
        acceso_original_restaurado: 'perfil.cesion_accion_acceso_original_restaurado',
        acceso_cedido_revocado: 'perfil.cesion_accion_acceso_cedido_revocado'
    };

    const formatUltimoMovimientoAccion = accion => {
        const translationKey = ultimoMovimientoLabels[accion];

        return translationKey ? t(translationKey) : (accion ?? '—');
    };

    const onCederSubmit = payload => {
        emit('ceder', payload);
    };

    const onRecuperarClick = () => {
        emit('recuperar');
    };
</script>
